import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../../../services/api";
import { toast } from "react-toastify";

/**
 * Hook personalizado para monitoreo VPS
 * Maneja todas las llamadas API y estados
 */
const useVPSMonitoring = () => {
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [systemStatus, setSystemStatus] = useState(null);
  const [fileAnalysis, setFileAnalysis] = useState([]);
  const [history, setHistory] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [databaseActivity, setDatabaseActivity] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(120000); // 2 minutos

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Obtener estado del sistema
   */
  const fetchSystemStatus = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.get("/vps/monitoring/status");
      if (isMountedRef.current) {
        if (data.success) {
          setSystemStatus(data.data);
        } else {
          throw new Error(data.error || "Error obteniendo estado del sistema");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo estado del sistema:", err);
        if (!autoRefresh) {
          toast.error(errorMessage);
        }
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [autoRefresh]);

  /**
   * Obtener análisis de archivos
   */
  const fetchFileAnalysis = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setError(null);
      const { data } = await api.get("/vps/files/analysis");
      if (isMountedRef.current) {
        if (data.success) {
          setFileAnalysis(data.data || []);
        } else {
          throw new Error(data.error || "Error obteniendo análisis de archivos");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo análisis de archivos:", err);
        if (!autoRefresh) {
          toast.error(errorMessage);
        }
      }
    }
  }, [autoRefresh]);

  /**
   * Obtener historial
   */
  const fetchHistory = useCallback(async (days = 7) => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setError(null);
      const { data } = await api.get(`/vps/monitoring/history?days=${days}`);
      if (isMountedRef.current) {
        if (data.success) {
          setHistory(data.data || []);
        } else {
          throw new Error(data.error || "Error obteniendo historial");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo historial:", err);
        if (!autoRefresh) {
          toast.error(errorMessage);
        }
      }
    }
  }, [autoRefresh]);

  /**
   * Obtener umbrales
   */
  const fetchThresholds = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setError(null);
      const { data } = await api.get("/vps/monitoring/thresholds");
      if (isMountedRef.current) {
        if (data.success) {
          setThresholds(data.data);
        } else {
          throw new Error(data.error || "Error obteniendo umbrales");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo umbrales:", err);
      }
    }
  }, []);

  /**
   * Actualizar umbrales
   */
  const updateThresholds = useCallback(async (thresholdsData) => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.put("/vps/monitoring/thresholds", thresholdsData);
      if (data.success) {
        setThresholds(data.data);
        toast.success("Umbrales actualizados correctamente");
        return true;
      } else {
        throw new Error(data.error || "Error actualizando umbrales");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Ejecutar limpieza
   * FASE 7: Soporta tanto ejecución directa (actionId) como background (queueId)
   */
  const executeCleanup = useCallback(async (type, params = {}) => {
    try {
      setLoading(true);
      setError(null);
      let endpoint = "";
      let method = "POST";

      switch (type) {
        case "binary-logs":
          endpoint = "/vps/cleanup/binary-logs";
          break;
        case "user-files":
          endpoint = "/vps/cleanup/user-files";
          break;
        case "emergency":
          endpoint = "/vps/cleanup/emergency";
          break;
        case "optimize":
          endpoint = "/vps/optimize/tables";
          break;
        case "refresh":
          endpoint = "/vps/monitoring/refresh";
          break;
        default:
          throw new Error("Tipo de limpieza no válido");
      }

      const { data } = await api[method.toLowerCase()](endpoint, params);
      if (data.success) {
        if (data.data?.queueId) {
          const queueId = data.data.queueId;
          toast.info("Acción iniciada en segundo plano");
          return {
            ...data,
            isBackground: true,
            queueId
          };
        }

        toast.success(data.message || "Operación completada correctamente");
        if (type === "refresh") {
          await fetchSystemStatus();
        } else {
          setTimeout(() => {
            fetchSystemStatus();
          }, 2000);
        }
        return data;
      } else {
        throw new Error(data.error || "Error ejecutando operación");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchSystemStatus]);

  /**
   * Eliminar archivos
   */
  const deleteFiles = useCallback(async (fileTypes = [], filePaths = []) => {
    try {
      setLoading(true);
      setError(null);
      console.log("[Frontend] Eliminando archivos:", { fileTypes, filePaths });
      
      // Usar api.request para DELETE con body (axios.delete a veces no envía body correctamente)
      const { data } = await api.request({
        method: "DELETE",
        url: "/vps/files/bulk",
        data: { fileTypes, filePaths }
      });
      
      console.log("[Frontend] Respuesta de eliminación:", data);
      
      if (data.success) {
        toast.success(data.message || "Archivos eliminados correctamente");
        // Refrescar análisis de archivos
        await fetchFileAnalysis();
        return data;
      } else {
        throw new Error(data.error || "Error eliminando archivos");
      }
    } catch (err) {
      console.error("[Frontend] Error eliminando archivos:", err);
      const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [fetchFileAnalysis]);

  /**
   * Listar archivos
   */
  const listFiles = useCallback(async (fileType = "mp4", limit = 100) => {
    try {
      setError(null);
      const { data } = await api.get(`/vps/files/list?type=${fileType}&limit=${limit}`);
      if (data.success) {
        return data;
      } else {
        throw new Error(data.error || "Error listando archivos");
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    }
  }, []);

  /**
   * Forzar actualización completa (ejecuta scripts y guarda en BD)
   */
  const forceRefresh = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setLoading(true);
      if (isMountedRef.current) setError(null);
      const { data } = await api.post("/vps/monitoring/refresh");
      if (isMountedRef.current) {
        if (data.success) {
          await Promise.all([
            fetchSystemStatus(),
            fetchFileAnalysis(),
            fetchThresholds()
          ]);
          return true;
        }
        throw new Error(data.error || "Error refrescando estado");
      }
      return false;
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error forzando refresh:", err);
        toast.error(errorMessage);
      }
      return false;
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [fetchSystemStatus, fetchFileAnalysis, fetchThresholds]);

  /**
   * Obtener métricas de actividad de base de datos
   */
  const fetchDatabaseActivity = useCallback(async () => {
    if (!isMountedRef.current) return;
    try {
      if (isMountedRef.current) setError(null);
      const { data } = await api.get("/vps/database-activity");
      if (isMountedRef.current) {
        if (data.success) {
          setDatabaseActivity(data.data);
        } else {
          throw new Error(data.error || "Error obteniendo métricas de actividad");
        }
      }
    } catch (err) {
      if (isMountedRef.current) {
        const errorMessage = err.response?.data?.error || err.message || "Error desconocido";
        setError(errorMessage);
        console.error("Error obteniendo métricas de actividad:", err);
        if (!autoRefresh) {
          toast.error(errorMessage);
        }
      }
    }
  }, [autoRefresh]);

  /**
   * Refrescar todos los datos (solo lectura, no ejecuta scripts)
   */
  const refreshAll = useCallback(async () => {
    if (!isMountedRef.current) return;
    await Promise.all([
      fetchSystemStatus(),
      fetchFileAnalysis(),
      fetchThresholds(),
      fetchDatabaseActivity()
    ]);
  }, [fetchSystemStatus, fetchFileAnalysis, fetchThresholds, fetchDatabaseActivity]);

  /**
   * Efecto para actualización automática
   */
  useEffect(() => {
    let isMounted = true;
    let intervalId = null;

    // Función wrapper para verificar si el componente está montado
    const safeRefresh = async () => {
      if (!isMounted) return;
      try {
        await refreshAll();
      } catch (err) {
        if (isMounted) {
          console.error("Error en refresh automático:", err);
        }
      }
    };

    // Cargar datos iniciales
    safeRefresh();

    // Configurar actualización automática
    if (autoRefresh) {
      intervalId = setInterval(() => {
        safeRefresh();
      }, refreshInterval);
    }

    // Cleanup
    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [autoRefresh, refreshInterval, refreshAll]);

  return {
    // Estados
    loading,
    error,
    systemStatus,
    fileAnalysis,
    history,
    thresholds,
    databaseActivity,
    autoRefresh,
    refreshInterval,

    // Acciones
    fetchSystemStatus,
    fetchFileAnalysis,
    fetchHistory,
    fetchThresholds,
    fetchDatabaseActivity,
    updateThresholds,
    executeCleanup,
    deleteFiles,
    listFiles,
    refreshAll,
    forceRefresh,

    // Configuración
    setAutoRefresh,
    setRefreshInterval
  };
};

export default useVPSMonitoring;
